import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { addDays, format, parseISO } from "date-fns";
import { de as deLocale, enGB as enLocale } from "date-fns/locale";
import { toast } from "sonner";
import { CheckCircle2, ChevronLeft, ChevronRight, XCircle } from "lucide-react";

import { availabilityService } from "../../../services/availability";
import { productsService } from "../../../services/products";
import { bookingPossibilityService } from "../../../services/bookingPossibility";
import { api } from "../../../services/api";
import { formatApiError } from "../../../services/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { DatePicker } from "../../../components/ui/DatePicker";
import { StatusBadge } from "../../../components/ui/StatusBadge";
import { EmptyState } from "../../../components/feedback/EmptyState";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { Skeleton } from "../../../components/feedback/Skeleton";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";

import { AvailabilityFilters } from "../components/AvailabilityFilters";
import { publicHolidaysService } from "../../holidays/services/holidays";
import { useAuth } from "../../../app/providers/AuthProvider";
import { usePersistedFilters } from "../../../hooks/usePersistedFilters";

// ── Constants ──────────────────────────────────────────────────────
const DEFAULT_RANGE_DAYS = 7;

function iso(d) {
  return format(d, "yyyy-MM-dd");
}
function splitCSV(v) {
  return v ? v.split(",").map((s) => s.trim()).filter(Boolean) : [];
}
function cellClass(available, total) {
  if (total <= 0) return "bg-offwhite text-text-secondary";
  if (available <= 0) return "bg-status-unavailable/25 text-status-unavailable font-semibold";
  if (available < total) return "bg-status-limited/20 text-charcoal";
  return "bg-surface text-text-primary";
}
function daysBetween(fromISO, untilISO) {
  return Math.round((parseISO(untilISO) - parseISO(fromISO)) / 86400000) + 1;
}
// A18.4 — Monday-of-week (ISO). Locale-agnostic; matches the existing
// KW-numbering used elsewhere in the UI (see admin holidays).
function mondayOf(d) {
  const dt = typeof d === "string" ? parseISO(d) : new Date(d);
  const day = dt.getDay(); // 0=Sun … 6=Sat
  const diff = (day + 6) % 7; // Mon offset
  const m = new Date(dt);
  m.setDate(dt.getDate() - diff);
  m.setHours(0, 0, 0, 0);
  return m;
}
function isoWeekNumber(d) {
  // ISO 8601 week number.
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  return Math.ceil(((dt - yearStart) / 86400000 + 1) / 7);
}

/**
 * A17 — Availability grid.
 * A18.4 — always renders a single ISO week (Mon–Sun). Prev/Next arrows
 * paginate across the weeks between from-date and until-date; no more
 * horizontal scroll and no more 30-day cap.
 */
function GridView({
  fromDate,
  untilDate,
  setFromDate,
  setUntilDate,
  filteredProducts,
  availability,
  loading,
  onCellClick,
  onClearFilters,
  isFiltered,
  filtersSlot,
  holidaysByDate = {},
  weekMonday,
  weekIndex,
  totalWeeks,
  onPrev,
  onNext,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? enLocale : deLocale;

  const days = useMemo(() => {
    if (!weekMonday) return [];
    return Array.from({ length: 7 }, (_, i) => addDays(weekMonday, i));
  }, [weekMonday]);

  const weekLabel = useMemo(() => {
    if (!weekMonday) return "";
    const sun = addDays(weekMonday, 6);
    const kw = isoWeekNumber(weekMonday);
    return `KW ${kw} · ${format(weekMonday, "d. MMM", { locale })} – ${format(sun, "d. MMM yyyy", { locale })}`;
  }, [weekMonday, locale]);

  const availById = useMemo(() => {
    const m = {};
    (availability?.products || []).forEach((p) => (m[p.product_id] = p));
    return m;
  }, [availability]);

  const prevDisabled = weekIndex <= 0;
  const nextDisabled = weekIndex >= totalWeeks - 1;

  return (
    <div className="space-y-4" data-testid="availability-grid-view">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5" data-testid="availability-range-from-wrapper">
          <Label className="text-xs text-text-secondary">{t("availability.grid_from_label")}</Label>
          <DatePicker
            value={fromDate}
            onChange={(d) => d && setFromDate(d)}
            required
            testId="availability-range-from"
          />
        </div>
        <div className="space-y-1.5" data-testid="availability-range-until-wrapper">
          <Label className="text-xs text-text-secondary">{t("availability.grid_until_label")}</Label>
          <DatePicker
            value={untilDate}
            onChange={(d) => {
              if (!d) return;
              if (fromDate && parseISO(d) < parseISO(fromDate)) {
                setUntilDate(fromDate);
              } else {
                setUntilDate(d);
              }
            }}
            required
            testId="availability-range-until"
          />
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {filtersSlot}
        </div>
      </div>

      {/* A18.4 — week paginator */}
      <div
        className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border-subtle bg-surface px-3 py-2"
        data-testid="availability-week-paginator"
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onPrev}
            disabled={prevDisabled}
            aria-label={t("availability.week_prev")}
            data-testid="availability-week-prev"
            className="inline-flex h-8 w-8 items-center justify-center rounded border border-border-subtle text-text-primary transition-colors hover:bg-offwhite disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={nextDisabled}
            aria-label={t("availability.week_next")}
            data-testid="availability-week-next"
            className="inline-flex h-8 w-8 items-center justify-center rounded border border-border-subtle text-text-primary transition-colors hover:bg-offwhite disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <span
            className="text-sm font-medium text-text-primary"
            data-testid="availability-week-label"
          >
            {weekLabel}
          </span>
        </div>
        <span
          className="text-xs text-text-secondary"
          data-testid="availability-week-indicator"
        >
          {t("availability.week_of_total", { current: weekIndex + 1, total: totalWeeks })}
        </span>
      </div>

      {loading ? (
        <div
          className="space-y-2 rounded-lg border border-border-subtle bg-surface p-4 shadow-card"
          data-testid="availability-grid-loading"
        >
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-6 w-12" />
              <Skeleton className="h-6 w-12" />
              <Skeleton className="h-6 w-12" />
            </div>
          ))}
        </div>
      ) : filteredProducts.length === 0 ? (
        isFiltered ? (
          <EmptyState
            title={t("availability.no_matching_products_title")}
            body={t("availability.no_matching_products_body")}
            actionLabel={t("availability.clear_filters")}
            onAction={onClearFilters}
            testId="availability-empty-filtered"
          />
        ) : (
          <EmptyState
            title={t("availability.no_products_title")}
            body={t("availability.no_products_body")}
            testId="availability-empty"
          />
        )
      ) : (
        <div className="rounded-lg border border-border-subtle bg-surface shadow-card">
          <table className="w-full text-sm" data-testid="availability-table">
            <thead className="bg-offwhite">
              <tr>
                <th className="w-72 bg-offwhite px-4 py-3 text-left text-[11px] uppercase tracking-wide text-text-secondary">
                  {t("availability.product_column")}
                </th>
                {days.map((d) => {
                  const dISO = iso(d);
                  const holiday = holidaysByDate[dISO];
                  return (
                    <th
                      key={dISO}
                      className="px-3 py-3 text-center text-[11px] uppercase tracking-wide text-text-secondary"
                      data-testid={holiday ? `availability-holiday-header-${dISO}` : undefined}
                      title={holiday ? t("admin.holidays.grid_tooltip", { name: holiday.name }) : undefined}
                    >
                      <div>{format(d, "EEE", { locale })}</div>
                      <div className="text-[10px] font-normal text-text-secondary">
                        {format(d, "d. MMM", { locale })}
                      </div>
                      {holiday && (
                        <div
                          className="mx-auto mt-1 line-clamp-2 max-w-[8ch] text-[10px] font-normal italic text-taupe"
                          data-testid={`availability-holiday-label-${dISO}`}
                        >
                          {holiday.name}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {filteredProducts.map((p) => {
                const av = availById[p.id];
                return (
                  <tr key={p.id} data-testid={`availability-row-${p.id}`}>
                    <td className="bg-surface px-4 py-2">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 flex-none overflow-hidden rounded bg-offwhite">
                          {p.image_url && (
                            <img src={p.image_url} alt="" className="h-full w-full object-cover" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-medium text-text-primary">{p.name}</div>
                        </div>
                      </div>
                    </td>
                    {days.map((d) => {
                      const dISO = iso(d);
                      const daily = (av?.daily || []).find((x) => x.date === dISO);
                      const total = p.in_stock || 0;
                      const available = daily ? daily.available : total;
                      const cls = cellClass(available, total);
                      const inactive = total <= 0;
                      const holiday = holidaysByDate[dISO];
                      const holidayTooltip = holiday
                        ? t("admin.holidays.grid_tooltip", { name: holiday.name })
                        : t("availability.cell_tooltip", { available, total });
                      const holidayOverlay = holiday
                        ? "bg-[repeating-linear-gradient(135deg,rgba(107,107,103,0.10)_0_6px,rgba(107,107,103,0)_6px_12px)] bg-offwhite/60"
                        : "";
                      return (
                        <td
                          key={dISO}
                          className={`relative px-3 py-2 text-center transition-colors ${cls} ${holidayOverlay} ${
                            inactive ? "cursor-default" : "cursor-pointer hover:opacity-90"
                          }`}
                          onClick={() =>
                            !inactive && onCellClick({ product: p, date: dISO })
                          }
                          title={holidayTooltip}
                          data-testid={`availability-cell-${p.id}-${dISO}`}
                          data-holiday={holiday ? "true" : undefined}
                        >
                          <div className="relative z-10 font-mono text-sm">{available}</div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * A17 — Booking possibility check tab.
 * Backend rules encapsulated on POST /api/booking-possibility.
 */
function BookingCheckView() {
  const { t } = useTranslation();
  const [fromDate, setFromDate] = useState(iso(new Date()));
  const [untilDate, setUntilDate] = useState(iso(addDays(new Date(), 3)));
  const [postal, setPostal] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { possible, reason_code?, cutoff_at?, delay_days? }

  const canSubmit =
    Boolean(fromDate) &&
    Boolean(untilDate) &&
    postal.trim().length >= 3 &&
    !busy;

  const submit = async () => {
    setResult(null);
    if (parseISO(untilDate) < parseISO(fromDate)) {
      toast.error(t("availability.booking_check.err_invalid_range"));
      return;
    }
    setBusy(true);
    try {
      const r = await bookingPossibilityService.check({
        from_date: fromDate,
        until_date: untilDate,
        postal_code: postal.trim(),
      });
      setResult(r);
    } catch (err) {
      toast.error(formatApiError(err, t("availability.booking_check.err_generic")));
    } finally {
      setBusy(false);
    }
  };

  const cutoffPretty = result?.cutoff_at
    ? new Date(result.cutoff_at).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : null;

  return (
    <div className="space-y-6" data-testid="availability-booking-check-view">
      <div className="rounded-lg border border-border-subtle bg-surface p-5 shadow-card">
        <h2 className="text-base font-semibold text-text-primary">
          {t("availability.booking_check.heading")}
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-text-secondary">
          {t("availability.booking_check.hint")}
        </p>

        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-text-secondary">
              {t("availability.booking_check.from_label")}
            </Label>
            <DatePicker
              value={fromDate}
              onChange={(d) => d && setFromDate(d)}
              required
              testId="booking-check-from"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-text-secondary">
              {t("availability.booking_check.until_label")}
            </Label>
            <DatePicker
              value={untilDate}
              onChange={(d) => {
                if (!d) return;
                if (fromDate && parseISO(d) < parseISO(fromDate)) {
                  setUntilDate(fromDate);
                } else {
                  setUntilDate(d);
                }
              }}
              required
              testId="booking-check-until"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-text-secondary">
              {t("availability.booking_check.postal_code_label")}
            </Label>
            <Input
              value={postal}
              onChange={(e) => setPostal(e.target.value.replace(/\s/g, ""))}
              inputMode="numeric"
              maxLength={10}
              placeholder="10117"
              data-testid="booking-check-postal"
              className="h-10 bg-surface border-border-subtle"
            />
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="booking-check-submit"
            className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy
              ? t("availability.booking_check.checking")
              : t("availability.booking_check.submit")}
          </Button>
        </div>
      </div>

      {result && result.possible && (
        <div
          className="flex gap-3 rounded-lg border border-status-available/40 bg-status-available/10 p-4"
          data-testid="booking-check-result-ok"
        >
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-status-available" />
          <div className="text-sm">
            <div className="font-semibold text-status-available">
              {t("availability.booking_check.ok_title")}
            </div>
            <div className="mt-1 text-text-primary">
              {t("availability.booking_check.ok_body")}
            </div>
            {cutoffPretty && (
              <div className="mt-2 text-xs text-text-secondary">
                {t("availability.booking_check.cutoff_label")}: {cutoffPretty}
                {typeof result.delay_days === "number" &&
                  ` · ${t("availability.booking_check.delay_days_label")}: ${t(
                    "availability.booking_check.delay_days_value",
                    { days: result.delay_days }
                  )}`}
              </div>
            )}
          </div>
        </div>
      )}

      {result && !result.possible && (
        <div
          className="flex gap-3 rounded-lg border border-status-unavailable/40 bg-status-unavailable/10 p-4"
          data-testid="booking-check-result-err"
        >
          <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-status-unavailable" />
          <div className="text-sm">
            <div className="font-semibold text-status-unavailable">
              {t("availability.booking_check.err_title")}
            </div>
            <div className="mt-1 text-text-primary">
              {t("availability.booking_check.err_body")}
            </div>
            {cutoffPretty && (
              <div className="mt-2 text-xs text-text-secondary">
                {t("availability.booking_check.cutoff_label")}: {cutoffPretty}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * A17 — In-Sheet consumer row. Click opens a center Dialog (no
 * navigation) with lightweight order details.
 */
function ConsumerRow({ consumer, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(consumer)}
      className="w-full cursor-pointer rounded-md border border-border-subtle p-3 text-left transition-colors hover:bg-offwhite"
      data-testid={`availability-panel-consumer-${consumer.order_id}`}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-semibold">
          {consumer.order_number ? `#${consumer.order_number}` : ""}
        </span>
        <StatusBadge status={consumer.status} size="sm" />
        <span className="ml-auto font-mono text-xs text-text-secondary">
          {consumer.quantity}×
        </span>
      </div>
      <div className="mt-1 truncate text-sm">{consumer.event_name}</div>
      <div className="text-xs text-text-secondary">
        {consumer.from_date} – {consumer.until_date}
        {consumer.customer_company ? ` · ${consumer.customer_company}` : ""}
      </div>
    </button>
  );
}

/**
 * A17 — Center dialog showing minimal order info. Deliberately omits
 * the status timeline / status history / damages sections (users don't
 * need them from the availability drilldown).
 *
 * A17.1 — Renders directly from the enriched consumer object. No
 * `/api/orders/{id}` fetch, which would 403 for cross-user rows (Standard
 * users only own their own orders but see everyone's here — intentional
 * per A12).
 */
function OrderQuickView({ consumer, open, onClose }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? enLocale : deLocale;
  const order = consumer || null;
  const renderAddr = (a) =>
    a && (
      <div className="text-xs leading-relaxed text-text-primary">
        <div className="font-semibold">{a.company}</div>
        <div>
          {a.street}
          <br />
          {a.postal_code} {a.city}
          <br />
          {a.country}
        </div>
        {(a.contact_email || a.contact_phone) && (
          <div className="mt-1 text-text-secondary">
            {a.contact_email}
            {a.contact_email && a.contact_phone ? " · " : ""}
            {a.contact_phone}
          </div>
        )}
      </div>
    );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl border-border-subtle bg-surface"
        data-testid="availability-order-quickview"
      >
        <DialogHeader>
          <DialogTitle className="text-left">
            {order?.order_number ? `#${order.order_number} · ` : ""}
            {order?.event_name || ""}
          </DialogTitle>
          <DialogDescription className="sr-only">Order details</DialogDescription>
        </DialogHeader>

        {order ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-text-secondary">
                  {t("orders.detail.period")}
                </div>
                <div className="text-text-primary">
                  {format(parseISO(order.from_date), "d. MMM yyyy", { locale })} –{" "}
                  {format(parseISO(order.until_date), "d. MMM yyyy", { locale })}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-text-secondary">
                  {t("orders.detail.bottles_count")}
                </div>
                <div className="text-text-primary">{order.bottles_count}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[11px] uppercase tracking-wide text-text-secondary">
                  {t("orders.detail.remarks")}
                </div>
                <div className="whitespace-pre-line text-text-primary">
                  {order.remarks?.trim() ? order.remarks : t("orders.detail.remarks_empty")}
                </div>
              </div>
            </div>

            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wide text-text-secondary">
                {t("orders.detail.products_heading")}
              </div>
              <ul className="divide-y divide-border-subtle rounded-md border border-border-subtle">
                {(order.items || []).map((it) => (
                  <li
                    key={it.product_id + it.product_name_snapshot}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                    data-testid={`quickview-item-${it.product_id}`}
                  >
                    <span className="text-text-primary">{it.product_name_snapshot}</span>
                    <span className="font-mono text-text-secondary">{it.quantity}×</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="rounded-md border border-border-subtle p-3">
                <div className="mb-1 text-[11px] uppercase tracking-wide text-text-secondary">
                  {t("orders.detail.customer_heading")}
                </div>
                {renderAddr(order.customer)}
              </div>
              <div className="rounded-md border border-border-subtle p-3">
                <div className="mb-1 text-[11px] uppercase tracking-wide text-text-secondary">
                  {t("orders.detail.delivery_heading")}
                </div>
                {renderAddr(order.delivery_address || order.customer)}
              </div>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default function AvailabilityPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = searchParams.get("view") === "booking_check" ? "booking_check" : "grid";

  // ── Date range state ──────────────────────────────────────────
  const today = iso(new Date());
  const [fromDate, setFromDate] = useState(
    searchParams.get("from") || today
  );
  const [untilDate, setUntilDate] = useState(
    searchParams.get("until") || iso(addDays(new Date(), DEFAULT_RANGE_DAYS))
  );

  // A18.4 — one-week-at-a-time paginator. Week 0 is the ISO week that
  // contains `fromDate`; week `totalWeeks-1` contains `untilDate`.
  const firstWeekMonday = useMemo(() => mondayOf(fromDate), [fromDate]);
  const lastWeekMonday = useMemo(() => mondayOf(untilDate), [untilDate]);
  const totalWeeks = useMemo(() => {
    const diff = Math.round((lastWeekMonday - firstWeekMonday) / (7 * 86400000));
    return Math.max(1, diff + 1);
  }, [firstWeekMonday, lastWeekMonday]);

  const [weekIndex, setWeekIndex] = useState(0);
  // Reset paginator whenever the user picks new bounds.
  useEffect(() => {
    setWeekIndex(0);
  }, [fromDate, untilDate]);
  // Guard against `weekIndex` going out of range (e.g. user shortens
  // the range while sitting on the last page).
  useEffect(() => {
    if (weekIndex > totalWeeks - 1) setWeekIndex(0);
  }, [weekIndex, totalWeeks]);

  const weekMonday = useMemo(
    () => addDays(firstWeekMonday, weekIndex * 7),
    [firstWeekMonday, weekIndex]
  );

  // Fetch bounds — always the full first-week-Monday → last-week-Sunday
  // span so edge days in partial weeks still have availability data.
  const fetchFromDate = useMemo(() => iso(firstWeekMonday), [firstWeekMonday]);
  const fetchUntilDate = useMemo(
    () => iso(addDays(lastWeekMonday, 6)),
    [lastWeekMonday]
  );

  const [view, setViewRaw] = useState(initialView);
  const setView = (v) => {
    setViewRaw(v);
    const params = new URLSearchParams(searchParams);
    if (v === "booking_check") params.set("view", v);
    else params.delete("view");
    setSearchParams(params, { replace: true });
  };

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drilldown, setDrilldown] = useState(null); // { product, date, consumers, loading }
  const [quickviewConsumer, setQuickviewConsumer] = useState(null);

  const [selectedBrands, setSelectedBrands] = useState(
    splitCSV(searchParams.get("brands"))
  );
  const [selectedCategories, setSelectedCategories] = useState(
    splitCSV(searchParams.get("categories"))
  );

  // URL sync — grid view only
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  useEffect(() => {
    if (view !== "grid") return;
    const params = new URLSearchParams(searchParamsRef.current);
    if (fromDate) params.set("from", fromDate); else params.delete("from");
    if (untilDate) params.set("until", untilDate); else params.delete("until");
    if (selectedBrands.length) params.set("brands", selectedBrands.join(","));
    else params.delete("brands");
    if (selectedCategories.length) params.set("categories", selectedCategories.join(","));
    else params.delete("categories");
    setSearchParams(params, { replace: true });
  }, [view, fromDate, untilDate, selectedBrands, selectedCategories]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let mounted = true;
    setProductsLoading(true);
    productsService
      .list({ page_size: 100 })
      .then((p) => {
        if (!mounted) return;
        setProducts(p.items || []);
        setProductsLoading(false);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(formatApiError(e, t("common.error_generic")));
        setProductsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [t]);

  // Master-data facets for filters
  const [brandOptionsSrc, setBrandOptionsSrc] = useState(null);
  const [categoryOptionsSrc, setCategoryOptionsSrc] = useState(null);
  useEffect(() => {
    const byLocale = (a, b) => a.localeCompare(b, undefined, { sensitivity: "base" });
    api
      .get("/brands")
      .then((r) => setBrandOptionsSrc((r.data.items || []).map((b) => b.name).sort(byLocale)))
      .catch(() => setBrandOptionsSrc(null));
    api
      .get("/categories")
      .then((r) => setCategoryOptionsSrc((r.data.items || []).map((c) => c.name).sort(byLocale)))
      .catch(() => setCategoryOptionsSrc(null));
  }, []);

  const brandOptions = useMemo(() => {
    if (brandOptionsSrc) return brandOptionsSrc;
    const s = new Set();
    products.forEach((p) => p.brand && s.add(p.brand));
    return [...s].sort();
  }, [brandOptionsSrc, products]);
  const categoryOptions = useMemo(() => {
    if (categoryOptionsSrc) return categoryOptionsSrc;
    const s = new Set();
    products.forEach((p) => p.category && s.add(p.category));
    return [...s].sort();
  }, [categoryOptionsSrc, products]);

  const filteredProducts = useMemo(() => {
    let list = products;
    if (selectedBrands.length) list = list.filter((p) => selectedBrands.includes(p.brand));
    if (selectedCategories.length) list = list.filter((p) => selectedCategories.includes(p.category));
    return list;
  }, [products, selectedBrands, selectedCategories]);

  const isFiltered = selectedBrands.length > 0 || selectedCategories.length > 0;

  // A16: persist brand + category filters
  const persistedFilters = usePersistedFilters({
    route: "availability",
    userId: user?.id,
    snapshot: { selectedBrands, selectedCategories },
    apply: (snap) => {
      if (Array.isArray(snap.selectedBrands)) setSelectedBrands(snap.selectedBrands);
      if (Array.isArray(snap.selectedCategories)) setSelectedCategories(snap.selectedCategories);
    },
  });

  // Fetch availability for the full first-Monday → last-Sunday span so
  // every rendered week has coverage.
  useEffect(() => {
    if (view !== "grid" || !fetchFromDate || !fetchUntilDate) return;
    if (filteredProducts.length === 0) {
      setAvailability({ products: [] });
      return;
    }
    const productIds = filteredProducts.map((p) => p.id);
    setAvailabilityLoading(true);
    availabilityService
      .grid(fetchFromDate, fetchUntilDate, productIds)
      .then((data) => {
        setAvailability(data);
        setAvailabilityLoading(false);
      })
      .catch((e) => {
        setError(formatApiError(e, t("common.error_generic")));
        setAvailabilityLoading(false);
      });
  }, [view, fetchFromDate, fetchUntilDate, filteredProducts, t]);

  // Public holidays overlay
  const [holidaysByDate, setHolidaysByDate] = useState({});
  useEffect(() => {
    if (view !== "grid" || !fetchFromDate || !fetchUntilDate) return;
    const start = parseISO(fetchFromDate);
    const end = parseISO(fetchUntilDate);
    const years = new Set([start.getFullYear(), end.getFullYear()]);
    let cancelled = false;
    Promise.all([...years].map((y) => publicHolidaysService.list(y)))
      .then((results) => {
        if (cancelled) return;
        const map = {};
        for (const r of results) {
          for (const h of r.items || []) map[h.date] = h;
        }
        setHolidaysByDate(map);
      })
      .catch(() => {
        if (!cancelled) setHolidaysByDate({});
      });
    return () => {
      cancelled = true;
    };
  }, [view, fetchFromDate, fetchUntilDate]);

  const openCell = useCallback(async ({ product, date }) => {
    setDrilldown({ product, date, loading: true, consumers: [] });
    try {
      const res = await availabilityService.consumers(product.id, date);
      setDrilldown({ product, date, loading: false, consumers: res.consumers || [] });
    } catch {
      setDrilldown({ product, date, loading: false, consumers: [] });
    }
  }, []);

  const closeDrilldown = () => setDrilldown(null);

  const toggleFrom = (setter) => (value) =>
    setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const clearFilters = () => {
    setSelectedBrands([]);
    setSelectedCategories([]);
    persistedFilters.clear();
  };

  return (
    <div className="space-y-6" data-testid="availability-page">
      <header>
        <h1 className="text-3xl font-semibold text-text-primary" data-testid="availability-title">
          {t("availability.title")}
        </h1>
        <p className="mt-2 text-sm text-text-secondary">{t("availability.subtitle")}</p>
      </header>

      <div
        className="flex items-center gap-1 border-b border-border-subtle"
        data-testid="availability-view-toggle"
      >
        <button
          type="button"
          onClick={() => setView("grid")}
          data-testid="availability-view-grid"
          className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
            view === "grid"
              ? "border-champagne text-charcoal"
              : "border-transparent text-text-secondary hover:text-charcoal"
          }`}
        >
          {t("availability.view_grid")}
        </button>
        <button
          type="button"
          onClick={() => setView("booking_check")}
          data-testid="availability-view-booking-check"
          className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
            view === "booking_check"
              ? "border-champagne text-charcoal"
              : "border-transparent text-text-secondary hover:text-charcoal"
          }`}
        >
          {t("availability.view_booking_check")}
        </button>
      </div>

      {error ? (
        <ErrorState title={t("common.error_generic")} body={error} testId="availability-error" />
      ) : view === "grid" ? (
        <GridView
          fromDate={fromDate}
          untilDate={untilDate}
          setFromDate={setFromDate}
          setUntilDate={setUntilDate}
          filteredProducts={filteredProducts}
          availability={availability}
          loading={productsLoading || availabilityLoading}
          onCellClick={openCell}
          onClearFilters={clearFilters}
          isFiltered={isFiltered}
          holidaysByDate={holidaysByDate}
          weekMonday={weekMonday}
          weekIndex={weekIndex}
          totalWeeks={totalWeeks}
          onPrev={() => setWeekIndex((i) => Math.max(0, i - 1))}
          onNext={() => setWeekIndex((i) => Math.min(totalWeeks - 1, i + 1))}
          filtersSlot={
            <AvailabilityFilters
              categories={categoryOptions}
              brands={brandOptions}
              selectedCategories={selectedCategories}
              selectedBrands={selectedBrands}
              onToggleCategory={toggleFrom(setSelectedCategories)}
              onToggleBrand={toggleFrom(setSelectedBrands)}
              onClear={clearFilters}
              isFiltered={isFiltered}
            />
          }
        />
      ) : (
        <BookingCheckView />
      )}

      {/* Right-side Sheet drilldown */}
      <Sheet open={Boolean(drilldown)} onOpenChange={(v) => !v && closeDrilldown()}>
        <SheetContent
          side="right"
          className="w-[420px] border-border-subtle bg-surface sm:max-w-md"
          data-testid="availability-panel"
        >
          <SheetHeader>
            <SheetTitle className="text-left">
              {drilldown?.product?.name}
              <div className="mt-1 text-xs font-normal text-text-secondary">
                {drilldown?.date}
              </div>
            </SheetTitle>
            <SheetDescription className="sr-only">
              {t("availability.consumers_heading")}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-3">
            {drilldown?.loading ? (
              <div className="text-sm text-text-secondary">{t("common.loading")}</div>
            ) : drilldown?.consumers?.length === 0 ? (
              <div className="text-sm text-text-secondary" data-testid="availability-panel-empty">
                {t("availability.no_consumers")}
              </div>
            ) : (
              (drilldown?.consumers || []).map((c) => (
                <ConsumerRow
                  key={c.order_id}
                  consumer={c}
                  onOpen={(x) => setQuickviewConsumer(x)}
                />
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Center dialog for order quick-view (from Sheet click) */}
      <OrderQuickView
        consumer={quickviewConsumer}
        open={Boolean(quickviewConsumer)}
        onClose={() => setQuickviewConsumer(null)}
      />
    </div>
  );
}
